import { useTranslation } from 'react-i18next'

import LangLink from '../components/LangLink'
import Section from '../components/Section'

interface IncotermRow {
  incoterm: string
  note: string
  carriage: string
  insurance: string
  exportCustoms: string
  importCustoms: string
}

interface PackagingRow {
  container: string
  looseBags: string
  palletized: string
}

// "CCLIMC" (the Chinese partner, the actual selling party - Intraveo only
// finds and qualifies the buyer) is a brand string and stays identical in
// every locale, so it is a safe test for the "who does this part" visual
// cue in the matrix. Anything else is the buyer ("The buyer" / "De koper" /
// "L'acheteur" / "Der Käufer" / "L'acquirente" / "买方").
function whoClass(value: string) {
  return value === 'CCLIMC' ? 'is-intraveo' : 'is-buyer'
}

// This page consolidates the logistics facts that also appear on the How to
// Order page and in the FAQ. It adds no new facts; the shared strings live in
// en.json (incoterms.*) and are translated like every other page.
function IncotermsPage() {
  const { t } = useTranslation()
  const rows = t('incoterms.rows', { returnObjects: true }) as IncotermRow[]
  const packagingRows = t('incoterms.packagingRows', {
    returnObjects: true,
  }) as PackagingRow[]
  const documents = t('incoterms.documents', { returnObjects: true }) as string[]

  const th = {
    carriage: t('incoterms.th.carriage'),
    insurance: t('incoterms.th.insurance'),
    exportCustoms: t('incoterms.th.exportCustoms'),
    importCustoms: t('incoterms.th.importCustoms'),
  }

  return (
    <div className="incoterms-page">
      {/* ---------- header ---------- */}
      <Section background="surface" space="md" className="incoterms-hero">
        <p className="eyebrow">{t('incoterms.eyebrow')}</p>
        <h1 className="display">{t('incoterms.title')}</h1>
        <p className="incoterms-lead">{t('incoterms.intro')}</p>
      </Section>

      {/* ---------- responsibilities matrix ---------- */}
      <Section background="beige" space="lg" className="incoterms-matrix-band">
        <h2>{t('incoterms.tableHeading')}</h2>
        <div className="incoterm-matrix-wrap">
          <table className="incoterm-matrix">
            <thead>
              <tr>
                <th>{t('incoterms.th.incoterm')}</th>
                <th>{th.carriage}</th>
                <th>{th.insurance}</th>
                <th>{th.exportCustoms}</th>
                <th>{th.importCustoms}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.incoterm}>
                  <th scope="row">
                    <span className="incoterm-code">{r.incoterm}</span>
                    <span className="incoterm-matrix-note">{r.note}</span>
                  </th>
                  <td data-label={th.carriage}>
                    <span className={whoClass(r.carriage)}>{r.carriage}</span>
                  </td>
                  <td data-label={th.insurance}>
                    <span className={whoClass(r.insurance)}>{r.insurance}</span>
                  </td>
                  <td data-label={th.exportCustoms}>
                    <span className={whoClass(r.exportCustoms)}>
                      {r.exportCustoms}
                    </span>
                  </td>
                  <td data-label={th.importCustoms}>
                    <span className={whoClass(r.importCustoms)}>
                      {r.importCustoms}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="incoterms-note">{t('incoterms.tableNote')}</p>
      </Section>

      <picture>
        <source srcSet="/partner/rack.webp" type="image/webp" />
        <img
          src="/partner/rack.jpg"
          alt={t('partnerChina.rackAlt')}
          className="incoterms-band-img"
          loading="lazy"
          width={1153}
          height={586}
        />
      </picture>

      {/* ---------- shipping route ---------- */}
      <Section background="surface" space="lg" className="incoterms-route-band">
        <h2>{t('incoterms.routeHeading')}</h2>
        <div className="incoterm-route" aria-hidden="true">
          <span className="incoterm-route-node">{t('incoterms.routeFrom')}</span>
          <span className="incoterm-route-link">
            <span className="incoterm-route-via">{t('incoterms.routeVia')}</span>
          </span>
          <span className="incoterm-route-node">{t('incoterms.routeTo')}</span>
        </div>
        <p>{t('incoterms.route')}</p>
      </Section>

      {/* ---------- packaging and container loads ---------- */}
      <Section background="beige" space="lg" className="incoterms-packaging-band">
        <h2>{t('incoterms.packagingHeading')}</h2>
        <p>{t('incoterms.packaging')}</p>
        <div className="incoterm-matrix-wrap">
          <table className="incoterm-matrix incoterm-matrix--packaging">
            <thead>
              <tr>
                <th>{t('incoterms.packagingTh.container')}</th>
                <th>{t('incoterms.packagingTh.looseBags')}</th>
                <th>{t('incoterms.packagingTh.palletized')}</th>
              </tr>
            </thead>
            <tbody>
              {packagingRows.map((r) => (
                <tr key={r.container}>
                  <th scope="row">
                    <span className="incoterm-code">{r.container}</span>
                  </th>
                  <td data-label={t('incoterms.packagingTh.looseBags')}>
                    {r.looseBags}
                  </td>
                  <td data-label={t('incoterms.packagingTh.palletized')}>
                    {r.palletized}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="incoterms-note">{t('incoterms.packagingNote')}</p>
      </Section>

      {/* ---------- shipping documents ---------- */}
      <Section background="surface" space="lg" className="incoterms-docs-band">
        <h2>{t('incoterms.documentsHeading')}</h2>
        <p>{t('incoterms.documentsIntro')}</p>
        <ul className="incoterm-docs">
          {documents.map((doc) => (
            <li key={doc}>{doc}</li>
          ))}
        </ul>
        <p className="incoterms-docs-extra">{t('incoterms.documentsExtra')}</p>
      </Section>

      <picture>
        <source srcSet="/partner/tanks.webp" type="image/webp" />
        <img
          src="/partner/tanks.jpg"
          alt={t('home.partner.imgAlt')}
          className="incoterms-band-img"
          loading="lazy"
          width={1163}
          height={584}
        />
      </picture>

      {/* ---------- customs + lead time: dark "at a glance" band ---------- */}
      <Section background="dark" space="lg" className="incoterms-glance-band">
        <div className="incoterm-glance-grid">
          <div className="incoterm-glance">
            <h2>{t('incoterms.customsHeading')}</h2>
            <p>{t('incoterms.customs')}</p>
          </div>
          <div className="incoterm-glance">
            <h2>{t('incoterms.leadTimeHeading')}</h2>
            <p>{t('incoterms.leadTime')}</p>
          </div>
        </div>
      </Section>

      {/* ---------- quote CTA band ---------- */}
      <Section background="bordeaux" space="md" className="incoterms-cta">
        <h2>{t('home.quote.heading')}</h2>
        <p>{t('home.quote.body')}</p>
        <LangLink to="/how-to-order" className="btn btn--on-dark">
          {t('incoterms.cta')}
        </LangLink>
      </Section>
    </div>
  )
}

export default IncotermsPage
